import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "@tanstack/react-router";
import { RTL_LANGS, WHATSAPP_URL, type Lang } from "@majourneys/shared";
import { Icon } from "@majourneys/icons";
import { PageSeo, SITE_ORIGIN } from "../../../shared/seo/page-seo";
import { BreadcrumbLd, siteUrl } from "../../../shared/seo/breadcrumb-ld";
import {
  LandingFaqs,
  LandingHero,
  LandingLinks,
  faqPageLd,
  type LandingFaq,
} from "../../../shared/seo/landing-sections";
import { panelStyle } from "../../../shared/ui/card-frame";
import { COMPARE_BRANDS, COMPARE_BRAND_BY_SLUG, isCompareBrand, type CompareBrandSlug } from "../data/brands";

/**
 * `/{lang}/compare/{brand}`: MaJourneys next to GetYourGuide or Viator.
 *
 * The content rule for this page: only say about MaJourneys what the product
 * actually does (activities paid on the day, stays paid online, our team on
 * WhatsApp, MaJourneys AI on our own catalogue), and only say general,
 * non-numeric things about the other platform. No competitor prices, fees,
 * review counts or cancellation windows: they change, and a wrong one is a
 * claim we cannot stand behind. When a line is unsure, it is not on the page.
 *
 * Every string is a literal key in `compare_page.*` so the locale checks can
 * see it. Copy that is the same for both brands takes `{{brand}}`; only the
 * SEO title, description and intro are written per brand.
 */

const NAVY = "#110E39";
const MUTED = "#5a5d77";
const GOLD_INK = "#8A6425";
const HAIRLINE = "rgba(17,14,57,0.09)";
const NAVY_GRADIENT = "linear-gradient(135deg, #2E2878 0%, #1A1454 48%, #110E39 100%)";

/** Row order of the side-by-side table. */
const ROWS = ["focus", "catalogue", "payment", "confirm", "support", "planning", "cancel"] as const;

const ROW_KEYS: Record<(typeof ROWS)[number], { label: string; us: string; them: string }> = {
  focus: {
    label: "compare_page.row_focus",
    us: "compare_page.row_focus_us",
    them: "compare_page.row_focus_them",
  },
  catalogue: {
    label: "compare_page.row_catalogue",
    us: "compare_page.row_catalogue_us",
    them: "compare_page.row_catalogue_them",
  },
  payment: {
    label: "compare_page.row_payment",
    us: "compare_page.row_payment_us",
    them: "compare_page.row_payment_them",
  },
  confirm: {
    label: "compare_page.row_confirm",
    us: "compare_page.row_confirm_us",
    them: "compare_page.row_confirm_them",
  },
  support: {
    label: "compare_page.row_support",
    us: "compare_page.row_support_us",
    them: "compare_page.row_support_them",
  },
  planning: {
    label: "compare_page.row_planning",
    us: "compare_page.row_planning_us",
    them: "compare_page.row_planning_them",
  },
  cancel: {
    label: "compare_page.row_cancel",
    us: "compare_page.row_cancel_us",
    them: "compare_page.row_cancel_them",
  },
};

const THEM_BETTER_KEYS = [
  "compare_page.them_better_1",
  "compare_page.them_better_2",
  "compare_page.them_better_3",
] as const;

const US_BETTER_KEYS = [
  "compare_page.us_better_1",
  "compare_page.us_better_2",
  "compare_page.us_better_3",
  "compare_page.us_better_4",
] as const;

const FAQ_KEYS = [
  ["compare_page.faq_1_q", "compare_page.faq_1_a"],
  ["compare_page.faq_2_q", "compare_page.faq_2_a"],
  ["compare_page.faq_3_q", "compare_page.faq_3_a"],
  ["compare_page.faq_4_q", "compare_page.faq_4_a"],
  ["compare_page.faq_5_q", "compare_page.faq_5_a"],
] as const;

const BRAND_COPY_KEYS: Record<CompareBrandSlug, { title: string; description: string; intro: string }> = {
  getyourguide: {
    title: "compare_page.getyourguide_seo_title",
    description: "compare_page.getyourguide_seo_description",
    intro: "compare_page.getyourguide_intro",
  },
  viator: {
    title: "compare_page.viator_seo_title",
    description: "compare_page.viator_seo_description",
    intro: "compare_page.viator_intro",
  },
};

/** The same city hubs the footer links, for one internal-link graph. */
const CITIES = ["marrakech", "casablanca", "fez", "agadir", "essaouira", "chefchaouen", "rabat", "tangier"] as const;

function titleize(slug: string): string {
  return slug.charAt(0).toUpperCase() + slug.slice(1);
}

function SuitsPanel({ title, items, accent }: { title: string; items: readonly string[]; accent: boolean }) {
  return (
    <div className="rounded-[24px] p-6" style={panelStyle(24)}>
      <h2 className="text-[18px] font-semibold" style={{ color: NAVY, letterSpacing: "-0.02em" }}>
        {title}
      </h2>
      <ul className="mt-4 flex flex-col gap-3">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-2.5 text-[14.5px] font-medium leading-[22px]" style={{ color: accent ? NAVY : MUTED }}>
            <Icon
              name="ri-check-line"
              size={18}
              aria-hidden="true"
              className="mt-[2px] shrink-0"
              style={{ color: accent ? GOLD_INK : MUTED }}
            />
            <span className="min-w-0">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ComparePage() {
  const { t } = useTranslation();
  const params = useParams({ strict: false }) as { lang?: string; brand?: string };
  const lang = params.lang ?? "en";
  const slug: CompareBrandSlug = params.brand && isCompareBrand(params.brand) ? params.brand : "getyourguide";
  const brand = COMPARE_BRAND_BY_SLUG[slug];
  const isRtl = (RTL_LANGS as readonly string[]).includes(lang as Lang);
  const arrow = isRtl ? "ri-arrow-left-line" : "ri-arrow-right-line";

  const copy = BRAND_COPY_KEYS[slug];
  const title = t(copy.title);
  const description = t(copy.description);
  const h1 = t("compare_page.h1", { brand: brand.name });

  const faqs = useMemo<LandingFaq[]>(
    () =>
      FAQ_KEYS.map(([q, a]) => ({
        question: t(q, { brand: brand.name }),
        answer: t(a, { brand: brand.name }),
      })),
    [t, brand.name],
  );

  const canonicalPath = `/${lang}/compare/${slug}`;
  const canonical = `${SITE_ORIGIN}${canonicalPath}`;

  const otherBrands = COMPARE_BRANDS.filter((b) => b !== slug).map((b) => ({
    href: `/${lang}/compare/${b}`,
    label: t("compare_page.h1", { brand: COMPARE_BRAND_BY_SLUG[b].name }),
  }));

  const cityLinks = CITIES.map((c) => ({
    href: `/${lang}/place/${c}`,
    label: t(`nav.city.${c}`, { defaultValue: titleize(c) }),
  }));

  return (
    <>
      <PageSeo
        title={title}
        description={description}
        canonicalPath={canonicalPath}
        lang={lang}
        ogType="article"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": `${canonical}#page`,
            url: canonical,
            name: title,
            description,
            inLanguage: lang,
            isPartOf: { "@id": `${SITE_ORIGIN}/#website` },
            about: [
              { "@type": "Organization", name: "MaJourneys", url: SITE_ORIGIN },
              { "@type": "Organization", name: brand.name, sameAs: brand.url },
            ],
          },
          faqPageLd(faqs, `${canonical}#faq`),
        ]}
      />
      <BreadcrumbLd items={[{ name: t("nav.home"), url: siteUrl(lang) }, { name: h1 }]} />

      <LandingHero
        eyebrow={t("compare_page.eyebrow")}
        title={h1}
        intro={t(copy.intro)}
        cta={{ href: `/${lang}/category/activities`, label: t("compare_page.cta_browse") }}
      />

      <section className="mx-auto mt-10 max-w-[1000px] px-4">
        <h2 className="text-center text-[20px] font-semibold" style={{ color: NAVY, letterSpacing: "-0.02em" }}>
          {t("compare_page.table_title")}
        </h2>
        <div className="mt-5 rounded-[24px]" style={panelStyle(24)}>
          <table className="w-full border-collapse text-start">
            <thead className="hidden md:table-header-group">
              <tr>
                <th scope="col" className="w-[26%] px-6 pb-3 pt-5 text-start text-[11px] font-bold uppercase" style={{ color: MUTED, letterSpacing: "0.08em" }}>
                  {t("compare_page.col_topic")}
                </th>
                <th scope="col" className="w-[37%] px-6 pb-3 pt-5 text-start text-[14px] font-bold" style={{ color: GOLD_INK }}>
                  MaJourneys
                </th>
                <th scope="col" className="w-[37%] px-6 pb-3 pt-5 text-start text-[14px] font-bold" style={{ color: NAVY }}>
                  {brand.name}
                </th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row, i) => {
                const keys = ROW_KEYS[row];
                const rule = i > 0 ? { borderTop: `1px solid ${HAIRLINE}` } : undefined;
                return (
                  <tr key={row} className="flex flex-col gap-2 px-5 py-4 md:table-row md:p-0" style={rule}>
                    <th
                      scope="row"
                      className="text-start text-[15px] font-bold md:px-6 md:py-4 md:align-top"
                      style={{ color: NAVY }}
                    >
                      {t(keys.label)}
                    </th>
                    <td className="text-[14.5px] font-medium leading-[22px] md:px-6 md:py-4 md:align-top" style={{ color: NAVY }}>
                      <span className="block text-[12px] font-bold md:hidden" style={{ color: GOLD_INK }}>
                        MaJourneys
                      </span>
                      {t(keys.us)}
                    </td>
                    <td className="text-[14.5px] font-medium leading-[22px] md:px-6 md:py-4 md:align-top" style={{ color: MUTED }}>
                      <span className="block text-[12px] font-bold md:hidden" style={{ color: NAVY }}>
                        {brand.name}
                      </span>
                      {t(keys.them, { brand: brand.name })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mx-auto mt-10 grid max-w-[1000px] gap-4 px-4 md:grid-cols-2">
        <SuitsPanel
          title={t("compare_page.them_better_title", { brand: brand.name })}
          items={THEM_BETTER_KEYS.map((k) => t(k))}
          accent={false}
        />
        <SuitsPanel
          title={t("compare_page.us_better_title")}
          items={US_BETTER_KEYS.map((k) => t(k))}
          accent
        />
      </section>

      <section className="mx-auto mt-10 max-w-[1000px] px-4">
        <div className="rounded-[24px] px-6 py-8 text-center sm:px-10" style={{ background: NAVY_GRADIENT }}>
          <h2 className="text-[22px] font-semibold text-white" style={{ letterSpacing: "-0.02em" }}>
            {t("compare_page.cta_title")}
          </h2>
          <p className="mx-auto mt-2 max-w-[560px] text-[15px] font-medium leading-[23px] text-white/85">
            {t("compare_page.cta_body")}
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <a
              href={`/${lang}/category/activities`}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-white px-6 text-[14.5px] font-semibold"
              style={{ color: NAVY }}
            >
              {t("compare_page.cta_browse")}
              <Icon name={arrow} size={16} aria-hidden="true" />
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-[44px] items-center gap-2 rounded-full px-6 text-[14.5px] font-semibold text-white"
              style={{ boxShadow: "inset 0 0 0 1.5px rgba(255,255,255,0.5)" }}
            >
              <Icon name="ri-whatsapp-line" size={18} aria-hidden="true" />
              {t("compare_page.cta_whatsapp")}
            </a>
          </div>
        </div>
      </section>

      <LandingLinks title={t("compare_page.also_title")} links={otherBrands} />
      <LandingLinks title={t("compare_page.cities_title")} links={cityLinks} />

      <LandingFaqs title={t("compare_page.faq_title", { brand: brand.name })} faqs={faqs} />

      <p className="mx-auto -mt-8 max-w-[800px] px-4 pb-16 text-[13px] font-medium leading-[20px]" style={{ color: MUTED }}>
        {t("compare_page.note")}
      </p>
    </>
  );
}
